import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import Home from "@/pages/marketing/Home";
import Features from "@/pages/marketing/Features";
import Pricing from "@/pages/marketing/Pricing";
import About from "@/pages/marketing/About";
import FAQ from "@/pages/marketing/FAQ";
import Contact from "@/pages/marketing/Contact";
import Legal from "@/pages/marketing/Legal";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Booking from "@/pages/Booking";
import WaitlistClaim from "@/pages/WaitlistClaim";
import AppShell from "@/pages/app/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import CalendarPage from "@/pages/app/CalendarPage";
import Customers from "@/pages/app/Customers";
import Employees from "@/pages/app/Employees";
import Services from "@/pages/app/Services";
import WaitingList from "@/pages/app/WaitingList";
import Billing from "@/pages/app/Billing";
import Settings from "@/pages/app/Settings";
import AdminPage from "@/pages/admin/AdminPage";

function FullLoader() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
    );
}

function Protected({ roles, children }) {
    const { ready, authed, user } = useAuth();
    if (!ready) return <FullLoader />;
    if (!authed) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user?.role)) return <Navigate to="/" replace />;
    return children;
}

function ScrollTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function TenantEntry({ children }) {
    // Redirect owners who haven't finished onboarding
    const { salon, user } = useAuth();
    if (user?.role === "owner" && salon && !salon.onboarding_completed) {
        return <Navigate to="/onboarding" replace />;
    }
    return children;
}

export default function App() {
    return (
        <BrowserRouter>
            <ScrollTop />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/legal/:doc" element={<Legal />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/book/:slug" element={<Booking />} />
                <Route path="/waitlist/claim/:token" element={<WaitlistClaim />} />

                <Route path="/onboarding" element={<Protected roles={["owner"]}><Onboarding /></Protected>} />

                <Route path="/app" element={<Protected roles={["owner", "employee"]}><TenantEntry><AppShell /></TenantEntry></Protected>}>
                    <Route index element={<Dashboard />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="customers" element={<Customers />} />
                    <Route path="employees" element={<Employees />} />
                    <Route path="services" element={<Services />} />
                    <Route path="waiting-list" element={<WaitingList />} />
                    <Route path="billing" element={<Billing />} />
                    <Route path="settings" element={<Settings />} />
                </Route>

                <Route path="/admin" element={<Protected roles={["crn_admin"]}><AdminPage /></Protected>} />

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}
